import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { getCustomEvent } from 'src/app/core/utils/http-request-utils';
import { LimitedCustomerBiller } from 'src/app/liabilities/models/limited-biller.model';
import { LoadingAction } from 'src/app/shared/models/loading-action.enum';
import { XsellOfferData } from '../../admin/models/xsell-offer.model';
import { ApiService } from '../../core/services/api.service';
import { Store } from '../../core/services/store.service';
import { CustomerBiller, ICanDeleteCustomerBiller, LiabilitySource } from '../models/model';
import { XsellOfferAdditionalInfo } from '../models/xsell-offer-additional-info.model';

@Injectable({
  providedIn: 'root',
})
export class CustomerBillerStoreService extends Store<CustomerBiller[]> {
  public refresh: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  private NAMESPACE = 'customer_biller';

  constructor(private api: ApiService) {
    super(null);
    this.state$ = this.refresh.pipe(switchMap(() => this.loadCustomerBillers()));
  }

  private loadCustomerBillers(): Observable<CustomerBiller[]> {
    return this.api
      .get<CustomerBiller[]>('/customerBillers', null, true, 'customerBiller')
      .pipe(tap((customerBillers) => this.setState(customerBillers)));
  }

  public getCustomerBillers(): Observable<CustomerBiller[]> {
    if (this.state) {
      return this.state$;
    }
    return this.loadCustomerBillers();
  }

  public getCustomerBillersWithSources(liabilitySources: LiabilitySource[]): Observable<LimitedCustomerBiller[]> {
    return this.api.get<LimitedCustomerBiller[]>('/customerBillers/limited', { liabilitySources });
  }

  public getCustomerBiller(customerBillerId: string): Observable<CustomerBiller> {
    return this.refresh.pipe(
      switchMap(() =>
        this.api.get<CustomerBiller>(`/customerBillers/${customerBillerId}`, null, true, 'customerBiller'),
      ),
    );
  }

  public fetchCustomerBiller(customerBillerId: string): Observable<CustomerBiller> {
    return this.api.get<CustomerBiller>(`/customerBillers/${customerBillerId}`);
  }

  public addCustomerBiller(billerId: string, source?: LiabilitySource): Observable<string> {
    var body: {
      billerId: string,
      liabilitySource: LiabilitySource
    } = {
      billerId: billerId,
      liabilitySource: null,
    };
    if (source) {
      body = {
        billerId: billerId,
        liabilitySource: source,
      };
    }
    return this.api.post<string>('/customerBillers', body);
  }

  public saveDisplayName(customerBillerId: string, displayName: string): Observable<unknown> {
    return this.api
      .put(`/customerBillers/${customerBillerId}/display-name`, { displayName })
      .pipe(tap(() => this.refreshConfiguration()));
  }

  public acceptXsellOfferConsent(customerBillerId: string): Observable<CustomerBiller[]> {
    return this.api.post(`/customerBillers/${customerBillerId}/xsell-offer-consent`).pipe(switchMap(() => this.state$));
  }

  public updateXsellOfferConsent(customerBillerId: string, additionalInfo: XsellOfferAdditionalInfo): Observable<void> {
    return this.api.post(`/customerBillers/${customerBillerId}/xsell-offer/additional-questions`, additionalInfo);
  }

  public saveOffer(xsellOffer: XsellOfferData): Observable<void> {
    return this.api.post(`/customerBillers/${xsellOffer.customerBillerId}/xsell-offer`, xsellOffer);
  }

  public isAdditionalQuestionVisible(customerBillerId: string): Observable<boolean> {
    return this.api
      .get<{ visible: boolean }>(`/customerBillers/${customerBillerId}/xsell-offer/additional-question/visible`)
      .pipe(map((resp) => resp.visible));
  }

  public setSubAccountsFolded(customerBillerId: string, folded: boolean): Observable<void> {
    const param = getCustomEvent(LoadingAction.SUB_ACCOUNT_FOLD_CHANGE, this.NAMESPACE, customerBillerId);
    const path = folded ? 'folded' : 'unfolded';
    return this.api.put(`/customerBillers/${customerBillerId}/userpanel-access/sub-accounts/${path}`, null, param);
  }

  public refreshAccount(customerBillerId: string): Observable<unknown> {
    return this.api
      .post<CustomerBiller>(`/customerBillers/${customerBillerId}/refreshAccount`)
      .pipe(tap(() => this.refreshConfiguration()));
  }

  public refreshDraftAccount(draftLiabilitySourceId: string): Observable<unknown> {
    return this.api
      .post<CustomerBiller>(`/customerBillers/${draftLiabilitySourceId}/refreshDraftAccount`)
      .pipe(tap(() => this.refreshConfiguration()));
  }

  public canDeleteOrArchive(customerBillerId: string): Observable<ICanDeleteCustomerBiller> {
    return this.api.get<ICanDeleteCustomerBiller>(`/customerBillers/${customerBillerId}/canDelete`);
  }

  public delete(customerBillerId: string): Observable<any> {
    const param = getCustomEvent(LoadingAction.DELETE, this.NAMESPACE, customerBillerId);

    return this.api
      .delete('/customerBillers/' + customerBillerId, null, param)
      .pipe(tap(() => this.refreshConfiguration()));
  }

  public deleteLiabilitySources(customerBillerId: string): Observable<any> {
    return this.api.delete(`/customerBillers/${customerBillerId}/liability-sources`);
  }

  public updateLiabilitySource(customerBillerId: string, liabilitySource: LiabilitySource): Observable<void> {
    return this.api.put(`/customerBillers/${customerBillerId}/liability-sources/${liabilitySource}`);
  }

  public saveEmailAccess(
    customerBillerId: string,
    emailAccessId: string,
    emailAccountId: string,
    formValue: any,
  ): Observable<void> {
    return this.api.put(
      `/customerBillers/${customerBillerId}/email-accesses/${emailAccessId}/accounts/${emailAccountId}`,
      formValue,
    );
  }

  public makeDraftPrimary(customerBillerId: string, draftId: string): Observable<void> {
    const param = getCustomEvent(LoadingAction.MAKE_DRAFT_PRIMARY, 'customer_biller', customerBillerId);
    return this.api.post(`/customerBillers/${customerBillerId}/liability-sources/drafts/${draftId}/primary`, null, {
      params: param,
    });
  }

  public updateManualConfiguration(
    customerBillerId: string,
    url: string,
    manualConfigurationId: string,
    data: any,
  ): Observable<void> {
    return this.api.put(`/customerBillers/${customerBillerId}${url}${manualConfigurationId}`, data);
  }

  public updateUserpanelAccess(
    customerBillerId: string,
    userPanelAccessId: string,
    formId: string,
    fields: Map<string, string>,
  ): Observable<void> {
    const data = { formId, fields };
    return this.api.put(`/customerBillers/${customerBillerId}/user-panel/${userPanelAccessId}/access`, data);
  }

  public passFollowUpCredentials(
    customerBillerId: string,
    userPanelAccessId: string,
    formId: string,
    fields: Map<string, string>,
  ): Observable<void> {
    const data = { credentials: { formId, fields } };
    return this.api.put(`/customerBillers/${customerBillerId}/user-panel/${userPanelAccessId}/follow-up`, data);
  }

  public deleteFollowUpCredentials(customerBillerId: string, userPanelAccessId: string): Observable<void> {
    return this.api.delete(`/customerBillers/${customerBillerId}/user-panel/${userPanelAccessId}/follow-up`);
  }

  public refreshConfiguration(): void {
    this.refresh.next(true);
  }

  public archive(customerBillerId: string): Observable<any> {
    return this.api.put(`/customerBillers/${customerBillerId}/archived`).pipe(tap(() => this.refreshConfiguration()));
  }

  public unarchive(customerBillerId: string): Observable<any> {
    return this.api.put(`/customerBillers/${customerBillerId}/unarchived`).pipe(tap(() => this.refreshConfiguration()));
  }
}
